import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const TOPICS = ["Articles", "Research", "Thought Leadership", "Blogs", "Resources", "Speaking", "Guidance"];

const Insights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/posts")
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="insights-page">
      <section className="bg-navy text-cream pt-40 pb-24 grain">
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="overline mb-6">Insights</p>
            <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-cream">
              A knowledge hub for children's services leaders.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-cream/75 leading-relaxed">
              Articles, research and thought leadership on safeguarding, governance, leadership
              and service transformation — establishing Charmaine Laville as a recognised voice
              within children's services.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {TOPICS.map((t) => (
                <span key={t} className="rounded-full border border-white/20 px-4 py-1.5 text-xs text-cream/70">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 rounded-full border-2 border-navy/20 border-t-navy animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <BookOpen className="mx-auto mb-4 text-gold" size={40} />
            <p>New insights are coming soon.</p>
          </div>
        ) : (
          <>
            {featured && (
              <Reveal>
                <Link
                  to={`/insights/${featured.slug}`}
                  data-testid="featured-post"
                  className="group grid gap-8 lg:grid-cols-2 lg:items-center mb-20"
                >
                  <div className="overflow-hidden rounded-3xl soft-shadow">
                    <img
                      src={featured.cover_image}
                      alt={featured.title}
                      className="h-[380px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div>
                    <span className="overline">{featured.category}</span>
                    <h2 className="mt-4 font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight group-hover:text-teal transition-colors duration-300">
                      {featured.title}
                    </h2>
                    <p className="mt-4 text-lg text-slate-600 leading-relaxed">{featured.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-navy font-medium link-underline">
                      Read article <ArrowUpRight size={16} className="text-gold" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            )}

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}>
                  <Link
                    to={`/insights/${p.slug}`}
                    data-testid={`post-card-${p.slug}`}
                    className="group block h-full rounded-3xl border border-slate-200 bg-white overflow-hidden transition-colors duration-300 hover:border-gold/60"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-7">
                      <span className="overline">{p.category}</span>
                      <h3 className="mt-3 font-serif text-xl text-navy leading-snug group-hover:text-teal transition-colors duration-300">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default Insights;
